import { Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { PaymentFooter } from '@woovi/ui';
import { convertToMoney } from '@woovi/utils';

import PaymentCheckoutCompanyData from './PaymentCheckoutCompanyData.tsx';
import PaymentCheckoutDeactivated from './PaymentCheckoutDeactivated.tsx';
import PaymentCheckoutPixAutomaticForm from './PaymentCheckoutPixAutomaticForm.tsx';
import PaymentCompanyLogo from '../logo/PaymentCompanyLogo.tsx';
import PaymentCompanySupport from '../support/PaymentCompanySupport.tsx';
import type { PaymentCheckoutPixAutomatic_checkout$key } from './__generated__/PaymentCheckoutPixAutomatic_checkout.graphql.ts';
import { PaymentCheckoutPixAutomaticInfo } from './PaymentCheckoutPixAutomaticInfo.tsx';

type PaymentCheckoutPixAutomaticProps = {
  checkout: PaymentCheckoutPixAutomatic_checkout$key;
};

const PaymentCheckoutPixAutomatic = (
  props: PaymentCheckoutPixAutomaticProps,
): ReactNode => {
  const { t } = useTranslation();

  const checkout = useFragment<PaymentCheckoutPixAutomatic_checkout$key>(
    graphql`
      fragment PaymentCheckoutPixAutomatic_checkout on Checkout {
        id
        active
        type
        value
        company {
          projectType
          nameFriendly
          ...PaymentCompanyLogo_company
          ...PaymentCheckoutCompanyData_company
          ...PaymentCompanySupport_company
        }
        ...PaymentCheckoutHeading_checkout
        ...PaymentCheckoutTitle_checkout
        ...PaymentCheckoutMethods_checkout
        ...PaymentCheckoutDeactivated_checkout
        ...PaymentCheckoutPixAutomaticInfo_subscription
        ...PaymentCheckoutPixAutomaticForm_checkout
      }
    `,
    props.checkout,
  );

  if (!checkout.active) {
    return <PaymentCheckoutDeactivated checkout={checkout} />;
  }

  const title = t('Approve the Pix Automatic of {{value}} to {{merchant}}', {
    value: convertToMoney(checkout.value),
    merchant: checkout.company?.nameFriendly,
  });

  return (
    <Stack spacing={3}>
      <PaymentCompanyLogo company={checkout.company} />
      <Typography variant='h5' fontWeight='bold' textAlign='center'>
        {title}
      </Typography>
      <PaymentCheckoutPixAutomaticInfo checkout={checkout} />
      <PaymentCheckoutPixAutomaticForm checkout={checkout} />
      <PaymentCheckoutCompanyData company={checkout.company} />
      <PaymentCompanySupport company={checkout.company} />
      <PaymentFooter projectType={checkout.company?.projectType} />
    </Stack>
  );
};

export default PaymentCheckoutPixAutomatic;
